<template>
  <div class="login-page">
    <app-navbar></app-navbar>
    <div class="wrapper wrapper-full-page">
      <div class="full-page login-page section-image">
        <!--   you can change the color of the filter page using: data-color="blue | azure | green | orange | red | purple" -->
        <div class="content">
          <div class="container">
            <div class="col-lg-4 col-md-6 ml-auto mr-auto mb-5">
              <card type="login" v-if="!totpTokenRequired">
                <h3 slot="header" class="header text-center">Login</h3>

                <fg-input
                  v-model="form.emailAddress"
                  name="emailAddress"
                  type="email"
                  data-vv-as="email address"
                  data-vv-delay="1000"
                  v-validate="formValidators.emailAddress"
                  :error="fieldError('emailAddress')"
                  addon-left-icon="nc-icon nc-single-02"
                  @keyup.enter="validate"
                  placeholder="Email Address"></fg-input>

                  <div class="input-group mb-3 rounded-0" :class="{ 'input-group-focus': isFocused }">
                    <div class="input-group-prepend rounded-0">
                      <span class="input-group-text border-right-0" :class="{ 'border-secondary': isFocused }">
                        <i class="nc-icon nc-key-25"></i>
                      </span>
                    </div>
                    <input class="form-control rounded-0 border-right-0" :class="{ 'border-secondary': isFocused }"
                      v-model="form.password" name="password" placeholder="Password"
                      :type="showPassword ? 'text' : 'password'" autocomplete="current-password"
                      @focus="isFocused = true" @blur="isFocused = false">
                    <div class="input-group-append rounded-0 border-left-0">
                      <span class="input-group-text border-left-0" :class="{ 'border-secondary': isFocused }"
                        @click="showPassword = !showPassword">
                        <i :class="showPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                      </span>
                    </div>
                  </div>

                <div class="alert alert-warning" v-if="errorMessage">
                  <span>{{ errorMessage }}</span>
                </div>

                <template class="mt-2">
                  <p-button
                    v-if="!loggingIn"
                    type="submit"
                    round
                    block
                    class="mb-3"
                    @click.prevent="validate">
                    Log in &nbsp;
                    <i class="fa fa-arrow-right"></i>
                  </p-button>
                  <p-button
                    v-if="loggingIn"
                    type="info"
                    round
                    block
                    class="mb-3"
                    disabled>
                    <i class="fa fa-spin fa-circle-o-notch"></i>&nbsp; Please
                    wait&hellip;
                  </p-button>

                  <p class="text-right">
                    <router-link
                      :to="`/forgot-password?emailAddress=${form.emailAddress}`">Forgot password?</router-link>
                  </p>
                </template>
              </card>
              <card v-else>
                <div class="alert alert-warning" v-if="errorMessage">
                  <span>{{ errorMessage }}</span>
                </div>
                <div slot="header">
                  <span class="text-center">
                    Enter the code from your authenticator app.
                  </span>
                </div>
                <form @submit.prevent>
                  <fg-input v-model="totpToken" name="totpToken" type="text" data-vv-as="code"
                    data-vv-delay="1000" v-validate="formValidators.totpToken" :error="fieldError('totpToken')"
                    addon-left-icon="nc-icon nc-key-25" @keyup.enter="validate" placeholder="Code"
                    autocomplete="one-time-code" input-ref="totpToken" ref="totpToken-input"></fg-input>
                </form>

                <p
                  style="cursor:pointer;"
                  class="text-center text-muted mb-0"
                  @click="showLostAccessInfo">
                  Lost access to your device?
                </p>

                <template slot="footer">
                  <p-button v-if="!loggingIn" type="submit" round block class="mb-3" @click.prevent="validate">
                    Submit &nbsp;
                    <i class="fa fa-arrow-right"></i>
                  </p-button>
                  <p-button v-if="loggingIn" type="info" round block class="mb-3" disabled>
                    <i class="fa fa-spin fa-circle-o-notch"></i>&nbsp; Please
                    wait&hellip;
                  </p-button>
                </template>

              </card>
            </div>
          </div>
        </div>
        <app-footer></app-footer>
        <div
          class="full-page-background"
          style="background: #F7971E; background: -webkit-linear-gradient(to left, #FFD200, #F7971E); background: linear-gradient(to left, #FFD200, #F7971E);">
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import moment from "moment";

import { Card, Checkbox, Button } from "src/components/UIComponents";
import AppNavbar from "./Layout/AppNavbar";
import AppFooter from "./Layout/AppFooter";
import getDeviceId from "src/device.js";
import config from "../../../../config";

export default {
  components: {
    Card,
    AppNavbar,
    AppFooter,
    [Checkbox.name]: Checkbox,
    [Button.name]: Button
  },
  methods: {
    toggleNavbar() {
      document.body.classList.toggle("nav-open");
    },
    closeMenu() {
      document.body.classList.remove("nav-open");
      document.body.classList.remove("off-canvas-sidebar");
    },
    validate() {
      this.$validator.validateAll().then(isValid => {
        this.$emit("on-submit", this.registerForm, isValid);
        if (isValid) {
          this.login();
        }
      });
    },
    fieldError(fieldName) {
      // clientside
      if (this.errors.first(fieldName)) {
        return this.errors.first(fieldName);
      }
    },
    maybeClearLocalStorage() {
      // NOTE: update this line when a change is made to VueTables structure etc.
      const MIN_LOCAL_STORAGE_CLEARANCE_DATE = "2023-08-07T16:45:00.000Z";
      const localStorageClearedAt = this.$ls.get("local_storage_cleared_at");
      if (localStorageClearedAt) {
        if (
          moment(localStorageClearedAt).isBefore(
            MIN_LOCAL_STORAGE_CLEARANCE_DATE
          )
        ) {
          localStorage.clear();
          this.$ls.set("local_storage_cleared_at", moment().toISOString());
        }
      } else {
        localStorage.clear();
        this.$ls.set("local_storage_cleared_at", moment().toISOString());
      }
    },
    login() {
      this.loggingIn = true;
      this.maybeClearLocalStorage();

      // create refresh token
      const body = {
        type: "refresh",
        grantType: "password",
        emailAddress: this.form.emailAddress,
        password: this.form.password,
        deviceIdentifier: getDeviceId()
      }

      if (this.totpTokenRequired) {
        body.mfaToken = this.totpToken;
      }

      this.axios
        .post(
          "/v1/tokens",
          body,
          { skipAuthRefresh: true }
        )
        .then((response) => {
          this.$store.dispatch("login");

          // set refresh token expiry
          const expSeconds = moment(response.data.expiresAt).unix() - moment().unix();
          this.$ls.set("refresh_token_expires_at", response.data.expiresAt, expSeconds * 1000);

          const accessTokenBody = {
            type: "access",
            grantType: "refresh_token",
            attachments: ["operator"],
            deviceIdentifier: getDeviceId()
          }

          // get access token
          return this.axios.post("/v1/tokens", accessTokenBody)
        })
        .then((response) => {
          const expSeconds =
            moment(response.data.expiresAt).unix() - moment().unix();
          this.$ls.set(
            "access_token_expires_at",
            response.data.expiresAt,
            expSeconds * 1000
          );

          // finally, get a user
          return this.axios.get("/v1/users/current");
        })
        .then((response) => {
          this.$store.commit("SET_USER", response.data);
          this.loggingIn = false;
          this.$router.push("/");

          // if totptoken is 24 chars long and access token has been
          // granted it would have to be  a recovery code that's been used
          if (this.totpToken && this.totpToken.length === 24) {
            this.showUsedRecoveryCodeInfo();
          }
        })
        .catch((error) => {
          console.error("error: ", error);
          this.loggingIn = false;
          if (error.response) {
            if (error.response.data.message == 'MFA token required') {
              this.totpTokenRequired = true;
              return;
            }
            //Checks if password is breached and if it is, forces user to reset password.
            if (error.response.data.message && error.response.data.message == 'Password appears in list of compromised passwords') {
              this.$swal({
                title: 'Password reset required',
                text: 'Your password was found in a database of exposed credentials. For security reasons, please reset your password.',
                icon: 'warning',
                showCancelButton: false,
                showCloseButton: false,
                confirmButtonText: 'Reset password now',
              }).then(() => {
                this.$router.push({
                  path: '/forgot-password',
                  query: {
                    emailAddress: `${this.form.emailAddress}`
                  }
                })
              }).catch((error) => {
                console.log('Error: ' + error)
              })
            }
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              if (error.response.data.message) {
                this.errorMessage = error.response.data.message;

                if (error.response.status === 400 && error.response.data.errors) {
                  this.validationErrors = error.response.data.errors;
                }
              } else {
                this.errorMessage = "Sorry, an unknown error occurred.";
              }
            } else if (error.request) {
              // The request was made but no response was received
              // `error.request` is an instance of XMLHttpRequest
              this.errorMessage = "Error - no response from server.";
            } else {
              // Something happened in setting up the request that triggered an Error
              this.errorMessage = "Error - request not sent";
            }
        })
    },
    showLostAccessInfo() {
      this.$swal({
        icon: "info",
        title: "Recover your account",
        html: `<p>If you have access to your recovery code which was issued when you enrolled for MFA, you can use it to login once more - just enter it where you would normally enter your MFA code. <br> <p>If you don't have your recovery code, please <a href="https://support.cabcard.services/contact-us"> contact support</a>.</p>`,
        confirmButtonColor: "#66615B",
        backdrop: 'rgba(0,0,0,0.4)'
      })
    },
    showUsedRecoveryCodeInfo() {
      this.$swal({
        icon: "warning",
        title: "Recovery code used",
        text: "You have used your recovery code to login, so multi-factor authentication (MFA) is now disabled on your account. Please re-enable it in your account settings if you wish to use MFA again.",
        confirmButtonColor: "#66615B",
        backdrop: 'rgba(0,0,0,0.4)'
      })
    }
  },
  data() {
    return {
      form: {
        emailAddress: "",
        password: ""
      },
      formValidators: {
        emailAddress: {
          required: true,
          email: true
        },
        password: {
          required: true
        }
      },
      isFocused: false,
      showPassword: false,
      validationErrors: {},
      errorMessage: null,
      loggingIn: false,
      totpToken: null,
      totpTokenRequired: false
    };
  },
  beforeDestroy() {
    this.closeMenu();
  }
};
</script>
<style></style>
